import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Award, ChevronDown, ChevronRight, Download, Loader2, CheckCircle2, Clock } from "lucide-react";

const RESULT = {
  passed: { label: "Flight test passed", cls: "bg-lime-100 text-lime-700 border-lime-200" },
  failed: { label: "Flight test failed", cls: "bg-red-100 text-red-700 border-red-200" },
  pending: { label: "Flight test pending", cls: "bg-amber-100 text-amber-700 border-amber-200" },
};

export default function TraineeCertificates({ trainee }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [issue, setIssue] = useState(null); // template being issued

  const load = useCallback(async () => {
    try { const r = await api.get(`/certificates/trainees/${trainee.id}/preview`); setData(r.data); }
    catch { /* best effort */ }
  }, [trainee.id]);
  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const result = data?.flight_test_result || trainee.flight_test_result || "pending";
  const st = RESULT[result] || RESULT.pending;
  const passed = result === "passed";

  return (
    <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden" data-testid="trainee-certificates">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50 transition-colors" data-testid="certificates-toggle" aria-expanded={open}>
        {open ? <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />}
        <Award className="h-5 w-5 text-lime-500 shrink-0" />
        <div className="flex-1">
          <h2 className="font-heading font-bold text-lg text-slate-900">Certificates</h2>
          <p className="text-xs text-slate-500 mt-0.5">Generate compliance & completion certificates as PDFs</p>
        </div>
        <Badge className={`border gap-1 ${st.cls}`}>{passed ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {st.label}</Badge>
      </button>

      {open && (
        <div className="px-5 pb-6 pt-2 border-t border-slate-100 space-y-3" data-testid="certificates-body">
          {!passed && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="cert-locked-note">
              Certificates unlock automatically once the student's flight test is marked <strong>Passed</strong> (in Student details). Admins can still generate them below.
            </div>
          )}
          {!data ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-lime-500" /></div>
          ) : data.templates.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No certificate templates match this student's program discipline. Add one on the Certificates page.</p>
          ) : (
            data.templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid={`cert-template-${t.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{t.title}</p>
                  <p className="text-xs text-slate-400">{t.name}{t.regulatory_ref ? ` · ${t.regulatory_ref}` : ""}</p>
                </div>
                <Button size="sm" variant={passed ? "default" : "outline"} className={passed ? "bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold shrink-0" : "shrink-0"} onClick={() => setIssue(t)} data-testid={`generate-cert-${t.id}`}>
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Generate
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      {issue && data && <IssueDialog trainee={trainee} tpl={issue} data={data} onClose={() => setIssue(null)} />}
    </div>
  );
}

function IssueDialog({ trainee, tpl, data, onClose }) {
  const ft = data.flight_test || {}, cert = data.cert || {};
  const [f, setF] = useState(() => ({
    issued_by: cert.issued_by || ft.issued_by || "",
    signature: cert.signature || "",
    date: cert.date || ft.date || new Date().toISOString().slice(0, 10),
    actuals: Object.fromEntries((tpl.requirement_rows || []).map((r) => [
      r.id, r.source_key === "total_hours" ? String(data.totals?.total_hours ?? "") : "",
    ])),
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setActual = (id, v) => setF((p) => ({ ...p, actuals: { ...p.actuals, [id]: v } }));

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/certificates/trainees/${trainee.id}/${tpl.id}/pdf`, f, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tpl.title.replace(/\s+/g, "_")}_${trainee.first_name || "student"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Certificate generated"); onClose();
    } catch { toast.error("Could not generate certificate"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cert-issue-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{tpl.title}</DialogTitle>
          <DialogDescription>For {trainee.first_name} {trainee.last_name}. Review the details, then download the PDF.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {tpl.show_hours_table && (tpl.requirement_rows || []).length > 0 && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Actual hours</Label>
              <div className="mt-2 space-y-2">
                {tpl.requirement_rows.map((r) => (
                  <div key={r.id} className="flex items-center gap-3">
                    <span className="text-sm text-slate-600 flex-1">{r.label} <span className="text-slate-400">(req {r.required})</span></span>
                    <Input value={f.actuals[r.id] ?? ""} onChange={(e) => setActual(r.id, e.target.value)} className="h-9 w-28" placeholder={r.source_key === "total_hours" ? "auto" : "0"} data-testid={`cert-actual-${r.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Issued by</Label>
              <Input value={f.issued_by} onChange={(e) => set("issued_by", e.target.value)} className="mt-1.5" data-testid="cert-issued-by" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Signatory</Label>
              <Input value={f.signature} onChange={(e) => set("signature", e.target.value)} className="mt-1.5" placeholder="Name" data-testid="cert-signature" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Date</Label>
              <Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" data-testid="cert-date" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={generate} disabled={busy} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="cert-download">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
